// WinnerDisplayPage.jsx — Sieger-Bildschirm fuer Beamer und zweiten Monitor.
// Gesteuert via localStorage + BroadcastChannel aus der Bracket-Verwaltung.
//
// Gestaltung: styles/leinwand.css, dieselbe wie Formen-, Siegerehrungs- und
// Bruchtest-Leinwand. Frueher hatte diese Seite ihre eigenen Goldtoene, ihre
// eigene Typografie und kein Kopfband — nacheinander auf derselben Leinwand
// gezeigt sah das aus wie zwei Programme.
import React, { useState, useEffect, useCallback } from 'react';
import '../styles/leinwand.css';
import './WinnerDisplayPage.css';

const LS_KEY  = 'tda-winner-state';
const CHANNEL = 'tda-winner';

export default function WinnerDisplayPage() {
  const [winnerData,   setWinnerData]   = useState(null);
  const [animKey,      setAnimKey]      = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Seite fuellt den Bildschirm, nichts scrollt weg
  useEffect(() => {
    const prev = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow             = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow             = prev.body;
    };
  }, []);

  // Auto-Vollbild — klappt, wenn das Fenster aus einer Nutzergeste kam
  useEffect(() => {
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Chat-Widget und alles ausserhalb der App ausblenden
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName))
          el.style.setProperty('display', 'none', 'important');
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // Sieger-Daten empfangen
  useEffect(() => {
    const apply = (raw) => {
      try {
        const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (d?.type === 'show') {
          setWinnerData(d);
          setAnimKey(k => k + 1);
        } else {
          setWinnerData(null);
        }
      } catch (e) { /* unbrauchbare Nachricht ignorieren */ }
    };

    apply(localStorage.getItem(LS_KEY));

    const onStorage = (e) => { if (e.key === LS_KEY) apply(e.newValue); };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => apply(e.data);
    } catch (e) { /* aeltere Browser: der storage-Weg genuegt */ }

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  }, []);

  if (!isFullscreen) return <SetupOverlay onFullscreen={toggleFullscreen} />;

  const grund = grundText(winnerData?.winReason);

  return (
    <div className="lw wd" onClick={toggleFullscreen}>
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Tiger &amp; Dragon Association</div>
        </div>
        <div className="lw-kopf-titel">勝利 SIEGER</div>
        <div className={`lw-kopf-status${winnerData ? '' : ' wartet'}`}>
          {winnerData ? grund : 'BEREIT'}
        </div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>勝</div>
        {winnerData
          ? <WinnerCard key={animKey} data={winnerData} />
          : <StandbyScreen />}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Division</div>
        <div className="lw-fuss-wert">{winnerData?.divisionCode || '—'}</div>
      </div>
    </div>
  );
}

function grundText(winReason) {
  if (!winReason) return 'SIEGER';
  const w = String(winReason).toLowerCase();
  if (w === 'ko')  return 'K. O.';
  if (w === 'tko') return 'T. K. O.';
  if (w === 'dq')  return 'DISQUALIFIKATION';
  return String(winReason).toUpperCase();
}

// ── Vor dem Vollbild ──────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  return (
    <div className="lw wd-setup">
      <div className="wd-setup-card">
        <div className="lw-kanji">勝</div>
        <div className="wd-setup-title">Sieger-Leinwand</div>
        <div className="wd-setup-sub">Bereit fuer Beamer oder zweiten Bildschirm</div>
        <button className="wd-setup-btn" onClick={onFullscreen}>⛶ Vollbild starten</button>
      </div>
    </div>
  );
}

// ── Wartezustand ──────────────────────────────────────────────────────
function StandbyScreen() {
  return (
    <>
      <div className="lw-name">TDA</div>
      <div className="lw-bereit-text">BEREIT</div>
    </>
  );
}

// ── Sieger ────────────────────────────────────────────────────────────
function WinnerCard({ data }) {
  const { vorname, nachname, verein, sideColor, belts } = data;

  const seite = sideColor === 'ao' ? 'ao' : sideColor === 'aka' ? 'aka' : null;

  return (
    <div className="wd-winner">
      {seite && <div className={`wd-seite ${seite}`} />}
      <div className="lw-name gross">{vorname} {nachname}</div>
      {verein && <div className="lw-unterzeile"><span className="lw-verein">{verein}</span></div>}
      {belts > 0 && <div className="wd-guertel">{belts} × Gürtel</div>}
    </div>
  );
}
